import BackButton from "@/components/BackButton";
import { MenuItem as TelegramMenuItem } from "@/components/MenuItem";
import TelegramSection from "@/components/TelegramSection";
import { useAlert } from "@/contexts/AlertContext";
import { useAlternativeUser } from "@/contexts/AlternativeUserContext";
import { useThemeMode } from "@/contexts/ThemeContext";
import { alternativeUserService } from "@/services/AlternativeUserService";
import { LanguageCode } from "@/types/language";
import {
  AttachMoney,
  AutoStories,
  CurrencyExchange,
  DarkMode,
  Favorite,
  Language,
  LightMode,
  Message,
  Share,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function SettingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, setUser } = useAlternativeUser();
  const { alert, toast } = useAlert();
  const { mode, toggleTheme } = useThemeMode();

  const menuItems = [
    {
      icon: AttachMoney,
      iconColor: "#C48C3A",
      iconBg: "rgba(196, 140, 58, 0.15)",
      label: t("currency"),
      path: "/setting/currency",
    },
    {
      icon: CurrencyExchange,
      iconColor: "#2E7D32",
      iconBg: "rgba(46, 125, 50, 0.15)",
      label: t("exchangeRate"),
      path: "/setting/exchange-rate",
    },
    {
      icon: Message,
      iconColor: "#3949AB",
      iconBg: "rgba(57, 73, 171, 0.15)",
      label: t("guestInviteMessage", { defaultValue: "សារអញ្ជើញភ្ញៀវ" }),
      path: "/setting/guest-invite",
    },
    {
      icon: AutoStories,
      iconColor: "#8E24AA",
      iconBg: "rgba(142, 36, 170, 0.15)",
      label: t("invitationRepresentative", {
        defaultValue: "តំណាងអ្នកអញ្ជើញ (Invitation Representative)",
      }),
      path: "/setting/invitation-template",
    },
    {
      icon: Share,
      iconColor: "#0288D1",
      iconBg: "rgba(2, 136, 209, 0.15)",
      label: t("linkPreviewTitle", { defaultValue: "ចំណងជើងតំណចែករំលែក" }),
      path: "/setting/link-preview",
    },
    {
      icon: Favorite,
      iconColor: "#E55353",
      iconBg: "rgba(229, 83, 83, 0.15)",
      label: t("thankYouMessage"),
      path: "/setting/thank-you",
    },
  ];

  const onLanguageChange = useCallback(
    async (lang: string) => {
      if (user?.id) {
        try {
          await alternativeUserService.updatePreferredLanguage(
            user.id,
            lang as LanguageCode,
          );
          setUser({
            ...user,
            preferredLanguage: lang as LanguageCode,
          });
          toast.success({
            message: t("notificationLanguageUpdated"),
          });
        } catch (error) {
          alert.error({
            message: t("failedToUpdateNotificationLanguage"),
          });
        }
      }
    },
    [user, alert, t],
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <Box sx={{ p: 2 }}>
        <BackButton />

        <Typography variant="h5" sx={{ my: 2, fontWeight: 700 }}>
          {t("setting")}
        </Typography>

        <TelegramSection>
          {menuItems.map((item, index) => (
            <TelegramMenuItem
              key={index}
              icon={item.icon}
              iconColor={item.iconColor}
              iconBg={item.iconBg}
              label={item.label}
              showDivider={index < menuItems.length - 1}
              onClick={() => navigate(item.path)}
            />
          ))}
        </TelegramSection>

        <TelegramSection title={t("preferencesAndSupport")}>
          {/* Theme Mode Row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.2,
              px: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  bgcolor:
                    mode === "dark"
                      ? "rgba(156, 39, 176, 0.15)"
                      : "rgba(196, 140, 58, 0.15)",
                  color: mode === "dark" ? "#BA68C8" : "#C48C3A",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {mode === "dark" ? (
                  <DarkMode sx={{ fontSize: 20 }} />
                ) : (
                  <LightMode sx={{ fontSize: 20 }} />
                )}
              </Box>
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, fontSize: "0.95rem" }}
              >
                {t("darkMode")}
              </Typography>
            </Box>

            <Switch
              checked={mode === "dark"}
              onChange={toggleTheme}
              color="primary"
            />
          </Box>

          <Divider sx={{ ml: "56px" }} />

          {/* Notification Language Row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.2,
              px: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  bgcolor: "rgba(196, 140, 58, 0.15)",
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Language sx={{ fontSize: 20 }} />
              </Box>
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, fontSize: "0.95rem" }}
              >
                {t("notificationLanguage")}
              </Typography>
            </Box>

            <Select
              size="small"
              sx={{
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "0.85rem",
                bgcolor: "action.hover",
                "& fieldset": {
                  border: "none",
                },
              }}
              value={user?.preferredLanguage || "en"}
              onChange={(e) => onLanguageChange(e.target.value)}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="kh">ខ្មែរ</MenuItem>
            </Select>
          </Box>
        </TelegramSection>
      </Box>
    </Box>
  );
}
